import React from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

type ModernDashboardWidgetProps = {
    className?: string;
    title: string;
    description?: string;
    value: string | number;
    subValue?: string;
    icon?: string;
    color?: string; // hex color or bootstrap color name
    chartSeries?: any[];
    chartColor?: string;
    onClick?: () => void;
};

export const ModernDashboardWidget: React.FC<ModernDashboardWidgetProps> = ({
    className,
    title,
    description,
    value,
    subValue,
    icon,
    color = 'primary',
    chartSeries,
    chartColor,
    onClick
}) => {
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');

    // Determine if color is a hex code or a bootstrap class suffix
    const isHex = color.startsWith('#');
    const bgClass = isHex ? '' : `bg-light-${color}`;
    const textClass = isHex ? '' : `text-${color}`;
    const iconClass = isHex ? '' : `text-${color}`;

    const chartOptions: ApexOptions = {
        chart: {
            type: 'area',
            height: 50,
            sparkline: {
                enabled: true
            },
        },
        stroke: {
            curve: 'smooth',
            width: 2
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.4,
                opacityTo: 0,
                stops: [0, 100]
            }
        },
        colors: [chartColor || (isHex ? color : '#009ef7')], // Default to primary blue if not specified
        tooltip: {
            fixed: {
                enabled: false
            },
            x: {
                show: false
            },
            y: {
                title: {
                    formatter: function (seriesName) {
                        return ''
                    }
                }
            },
            marker: {
                show: false
            }
        }
    };

    return (
        <div 
            className={`card h-100 ${className || ''} ${onClick ? 'cursor-pointer hover-elevate-up' : ''}`} 
            onClick={onClick}
            style={{ backgroundColor: isDarkMode ? '#1e1e2d' : '' }}
        >
            <div className="card-body p-5">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                        {icon && (
                            <div className={`symbol symbol-40px me-3 ${bgClass}`} style={isHex ? {backgroundColor: `${color}20`} : {}}>
                                <div className="symbol-label">
                                    <i className={`bi ${icon} fs-2 ${iconClass}`} style={isHex ? {color: color} : {}}></i>
                                </div>
                            </div>
                        )}
                        <div className="d-flex flex-column">
                            <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'} fw-bold fs-6`}>{title}</span>
                            {description && <span className={`${isDarkMode ? 'text-white opacity-75' : 'text-gray-600'} fw-semibold fs-8`}>{description}</span>}
                        </div>
                    </div>
                    {/* Optional Menu Icon placeholder - could be functional later */}
                    <div className="btn btn-sm btn-icon btn-color-gray-400 btn-active-color-primary">
                        <i className="bi bi-three-dots fs-3"></i>
                    </div>
                </div>

                {/* Content */}
                <div className="d-flex align-items-end justify-content-between">
                    <div>
                        <span className={`fs-2hx fw-bolder ${isDarkMode ? 'text-white' : 'text-gray-900'} d-block lh-1 mb-1`}>{value}</span>
                        {subValue && <span className="text-gray-600 fw-bold fs-7">{subValue}</span>}
                    </div>
                    
                    {/* Mini Chart */}
                    {chartSeries && (
                        <div className="flex-grow-1 ms-3" style={{ maxWidth: '120px', marginBottom: '-5px' }}>
                            <Chart
                                options={chartOptions}
                                series={chartSeries}
                                type="area"
                                height={50}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
